import React, { useMemo } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

function emptyDirectory() {
  return {
    schema_version: 1,
    region: 'AU',
    banks: [],
    bsb_prefixes: [],
  }
}

function normalizeFromSettings(value) {
  if (value == null || value === '') return emptyDirectory()
  const raw = typeof value === 'string' ? (() => { try { return JSON.parse(value) } catch { return null } })() : value
  if (!raw || typeof raw !== 'object') return emptyDirectory()

  const banks = (Array.isArray(raw.banks) ? raw.banks : []).map((b, idx) => ({
    id: String(b.id ?? b.bank_id ?? '').trim(),
    name: String(b.name ?? '').trim(),
    in_picker: b.in_picker ?? b.inPicker ?? true,
    sort_order: Number(b.sort_order ?? b.sortOrder ?? (idx + 1) * 10),
  }))

  const bsb_prefixes = (Array.isArray(raw.bsb_prefixes) ? raw.bsb_prefixes : (Array.isArray(raw.bsbPrefixes) ? raw.bsbPrefixes : []))
    .map((p) => ({
      prefix: String(p.prefix ?? '').replace(/\D/g, ''),
      bank_id: String(p.bank_id ?? p.bankId ?? '').trim(),
    }))

  return {
    schema_version: Number(raw.schema_version ?? raw.schemaVersion ?? 1) || 1,
    region: String(raw.region ?? 'AU').trim() || 'AU',
    banks,
    bsb_prefixes,
  }
}

function toStorageShape(dir) {
  return {
    schema_version: dir.schema_version,
    region: dir.region,
    banks: dir.banks.map((b) => ({
      id: b.id,
      name: b.name,
      in_picker: !!b.in_picker,
      sort_order: Number(b.sort_order) || 0,
    })),
    bsb_prefixes: dir.bsb_prefixes.map((p) => ({
      prefix: String(p.prefix).replace(/\D/g, ''),
      bank_id: p.bank_id,
    })),
  }
}

export default function BankDirectoryEditor({ value, onChange }) {
  const dir = useMemo(() => normalizeFromSettings(value), [value])
  const bankIds = dir.banks.map((b) => b.id).filter(Boolean)

  const update = (next) => onChange(toStorageShape(next))

  const updateBank = (idx, patch) => {
    const banks = dir.banks.map((b, i) => (i === idx ? { ...b, ...patch } : b))
    update({ ...dir, banks })
  }

  const addBank = () => {
    const n = dir.banks.length + 1
    update({
      ...dir,
      banks: [
        ...dir.banks,
        { id: '', name: '', in_picker: true, sort_order: n * 10 },
      ],
    })
  }

  const removeBank = (idx) => {
    const removedId = dir.banks[idx]?.id
    const banks = dir.banks.filter((_, i) => i !== idx)
    const bsb_prefixes = removedId
      ? dir.bsb_prefixes.filter((p) => p.bank_id !== removedId)
      : dir.bsb_prefixes
    update({ ...dir, banks, bsb_prefixes })
  }

  const updatePrefix = (idx, patch) => {
    const bsb_prefixes = dir.bsb_prefixes.map((p, i) => (i === idx ? { ...p, ...patch } : p))
    update({ ...dir, bsb_prefixes })
  }

  const addPrefix = () => {
    update({
      ...dir,
      bsb_prefixes: [...dir.bsb_prefixes, { prefix: '', bank_id: bankIds[0] || '' }],
    })
  }

  const removePrefix = (idx) => {
    update({ ...dir, bsb_prefixes: dir.bsb_prefixes.filter((_, i) => i !== idx) })
  }

  return (
    <div className="py-3 border-t border-gray-100 mt-1 space-y-4">
      <div>
        <div className="text-sm font-medium text-gray-900">Bank directory</div>
        <p className="text-xs text-gray-500 mt-0.5 mb-2">
          PaySafeGo mobile EFT: bank name pick-list and BSB → bank auto-fill (longest prefix wins).
          Banks with <strong>In picker</strong> off are BSB-only (e.g. Citibank).
        </p>
        <div className="flex gap-3 mb-3">
          <label className="text-xs text-gray-600">
            Region
            <input
              type="text"
              className="mt-0.5 block w-20 px-2 py-1 text-sm border border-gray-300 rounded"
              value={dir.region}
              onChange={(e) => update({ ...dir, region: e.target.value })}
            />
          </label>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-900">Banks</div>
          <button type="button" onClick={addBank} className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900">
            <PlusIcon className="h-4 w-4" /> Add bank
          </button>
        </div>
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">ID</th>
                <th className="px-2 py-1.5 text-left font-medium">Name</th>
                <th className="px-2 py-1.5 text-left font-medium">Sort</th>
                <th className="px-2 py-1.5 text-left font-medium">In picker</th>
                <th className="px-2 py-1.5 w-8" />
              </tr>
            </thead>
            <tbody>
              {dir.banks.length === 0 && (
                <tr><td colSpan={5} className="px-2 py-3 text-gray-400 text-center">No banks — add at least one</td></tr>
              )}
              {dir.banks.map((b, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="px-2 py-1">
                    <input
                      className="w-full px-1.5 py-1 border border-gray-200 rounded font-mono"
                      value={b.id}
                      placeholder="cba"
                      onChange={(e) => updateBank(idx, { id: e.target.value.trim().toLowerCase().replace(/\s+/g, '_') })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      className="w-full min-w-[140px] px-1.5 py-1 border border-gray-200 rounded"
                      value={b.name}
                      placeholder="Commonwealth Bank"
                      onChange={(e) => updateBank(idx, { name: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1 w-16">
                    <input
                      type="number"
                      className="w-full px-1.5 py-1 border border-gray-200 rounded"
                      value={b.sort_order}
                      onChange={(e) => updateBank(idx, { sort_order: Number(e.target.value) || 0 })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={!!b.in_picker}
                      onChange={(e) => updateBank(idx, { in_picker: e.target.checked })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <button type="button" onClick={() => removeBank(idx)} className="text-red-600 hover:text-red-800" aria-label="Remove bank">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-900">BSB prefixes</div>
          <button type="button" onClick={addPrefix} disabled={bankIds.length === 0} className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 disabled:opacity-40">
            <PlusIcon className="h-4 w-4" /> Add prefix
          </button>
        </div>
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Prefix</th>
                <th className="px-2 py-1.5 text-left font-medium">Bank</th>
                <th className="px-2 py-1.5 w-8" />
              </tr>
            </thead>
            <tbody>
              {dir.bsb_prefixes.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-3 text-gray-400 text-center">No BSB prefixes</td></tr>
              )}
              {dir.bsb_prefixes.map((p, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="px-2 py-1 w-24">
                    <input
                      className="w-full px-1.5 py-1 border border-gray-200 rounded font-mono"
                      value={p.prefix}
                      placeholder="06"
                      maxLength={3}
                      onChange={(e) => updatePrefix(idx, { prefix: e.target.value.replace(/\D/g, '') })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      className="w-full px-1.5 py-1 border border-gray-200 rounded"
                      value={p.bank_id}
                      onChange={(e) => updatePrefix(idx, { bank_id: e.target.value })}
                    >
                      <option value="">Select bank…</option>
                      {dir.banks.filter((b) => b.id).map((b) => (
                        <option key={b.id} value={b.id}>{b.name || b.id}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <button type="button" onClick={() => removePrefix(idx)} className="text-red-600 hover:text-red-800" aria-label="Remove prefix">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
